/**
 * Cart Page JavaScript
 * AJAX quantity updates with circle loading spinners
 */

class CartPageManager {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('[data-cart-form]');
    this.itemsContainer = container.querySelector('[data-cart-items]');
    this.itemsScrollContainer = container.querySelector('[data-cart-items-scroll]');
    this.summaryContainer = container.querySelector('[data-cart-summary]');
    this.noteInput = container.querySelector('[data-cart-note]');
    this.headerSectionId = Object.keys(window.theme?.sections?.header || {})[0] || null;
    this.sectionsUrl = window.location.pathname + window.location.search;
    
    this.init();
  }
  
  init() {
    this.bindEvents();
  }
  
  bindEvents() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }

    // Quantity change buttons
    this.container.querySelectorAll('[data-qty-change]').forEach(btn => {
      btn.addEventListener('click', this.handleQuantityChange.bind(this));
    });
    
    // Typed quantity edits
    this.container.querySelectorAll('[data-qty-value]').forEach(input => {
      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '');
      });
      input.addEventListener('change', this.handleQuantityInput.bind(this));
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        input.blur();
      });
    });

    // Remove item buttons
    this.container.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', this.handleRemoveItem.bind(this));
    });
    
    // Note auto-save
    if (this.noteInput) {
      let noteTimeout;
      this.noteInput.addEventListener('input', () => {
        clearTimeout(noteTimeout);
        noteTimeout = setTimeout(() => {
          this.updateNote(this.noteInput.value);
        }, 500);
      });
    }
  }

  async handleFormSubmit(event) {
    if (event.submitter?.name !== 'checkout' || !window.CartApi?.validateCheckout) return;

    event.preventDefault();

    try {
      await CartApi.validateCheckout();
      CartApi.submitFormWithSubmitter?.(this.form, event.submitter);
    } catch (error) {
      this.showError(error.message);
    }
  }

  getCartSectionsPayload({ quantity, line = null, id = null }) {
    const payload = { quantity };

    if (line != null && !Number.isNaN(line)) {
      payload.line = line;
    } else if (id != null && !Number.isNaN(id)) {
      payload.id = id;
    }

    if (this.headerSectionId) {
      payload.sections = this.headerSectionId;
      payload.sections_url = this.sectionsUrl;
    }

    return payload;
  }
  
  handleQuantityChange(event) {
    const button = event.currentTarget;
    const item = button.closest('[data-cart-item]');
    const qtyValueEl = item.querySelector('[data-qty-value]');
    const change = parseInt(button.dataset.qtyChange);
    // defaultValue holds the last committed quantity; value may be mid-edit
    const currentQty = parseInt(qtyValueEl.defaultValue, 10);

    return this.updateQuantity(item, currentQty + change, button);
  }

  handleQuantityInput(event) {
    const qtyValueEl = event.currentTarget;
    const item = qtyValueEl.closest('[data-cart-item]');

    return this.updateQuantity(item, Number.parseInt(qtyValueEl.value, 10));
  }

  async updateQuantity(item, newQty, button = null) {
    const line = Number.parseInt(item.dataset.line || '', 10);
    const variantId = item.dataset.variantId;
    const qtyValueEl = item.querySelector('[data-qty-value]');
    const currentQty = parseInt(qtyValueEl.defaultValue, 10);
    const restoreInput = () => {
      qtyValueEl.value = `${currentQty}`;
    };

    if (item.dataset.updating === 'true') {
      restoreInput();
      return;
    }

    // Empty, zero or unchanged input just snaps back; removing stays on the trash button
    if (!Number.isInteger(newQty) || newQty < 1 || newQty === currentQty) {
      restoreInput();
      return;
    }

    try {
      CartApi.validateVariantMinimumQuantity?.(item.dataset, newQty);
    } catch (error) {
      this.showError(error.message);
      restoreInput();
      return;
    }

    if (newQty > currentQty) {
      try {
        const cart = await CartApi.getCart();

        if (window.CartApi?.isHighRiskManagementEnabled?.()) {
          const projectedTotal = CartApi.getItemCount(cart) - currentQty + newQty;
          await CartApi.validateProjectedCartQuantity(projectedTotal);
        }

        CartApi.validateCartVariantQuantity({
          cart,
          variantId,
          inventoryData: item.dataset,
          nextQuantity: newQty,
          excludeLine: line
        });
      } catch (error) {
        this.showError(error.message);
        restoreInput();
        return;
      }
    }

    // Save scroll position
    const scrollTop = this.itemsScrollContainer ? this.itemsScrollContainer.scrollTop : 0;

    // Show loading state
    item.dataset.updating = 'true';
    if (button) {
      button.classList.add('is-loading');
      button.disabled = true;
    }
    qtyValueEl.disabled = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(this.getCartSectionsPayload({ quantity: newQty, line }))
      });

      const cart = await response.json();

      if (response.ok) {
        // Update quantity display and remember it as the committed value
        qtyValueEl.value = newQty;
        qtyValueEl.defaultValue = `${newQty}`;

        // Update cart totals
        this.updateCartSummary(cart);

        // Update item price if needed
        this.updateItemPrice(item, cart, line);

        // Restore scroll position
        if (this.itemsScrollContainer) {
          this.itemsScrollContainer.scrollTop = scrollTop;
        }

        // Dispatch cart updated event with cart data
        this.dispatchCartUpdateEvent(cart);
      } else {
        throw new Error(cart.message || cart.description || 'Failed to update quantity');
      }
    } catch (error) {
      console.error('Cart update error:', error);
      restoreInput();
      this.showError(error.message);
    } finally {
      // Remove loading state
      delete item.dataset.updating;
      qtyValueEl.disabled = false;
      if (button) {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }
  }

  async handleRemoveItem(event) {
    const button = event.currentTarget;
    const item = button.closest('[data-cart-item]');
    const line = Number.parseInt(item.dataset.line || '', 10);
    
    // Show loading state
    item.style.opacity = '0.5';
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(this.getCartSectionsPayload({ quantity: 0, line }))
      });
      
      const cart = await response.json();
      
      if (response.ok) {
        // Animate removal
        item.style.transform = 'translateX(-20px)';
        item.style.opacity = '0';
        
        setTimeout(() => {
          item.remove();
          this.reindexCartItems();
          
          // Update cart totals
          this.updateCartSummary(cart);
          
          // Check if cart is empty
          if (cart.item_count === 0) {
            this.handleEmptyCart();
          }
          
          // Dispatch cart updated event
          this.dispatchCartUpdateEvent(cart);
        }, 300);
      } else {
        throw new Error(cart.message || 'Failed to remove item');
      }
    } catch (error) {
      console.error('Remove item error:', error);
      item.style.opacity = '1';
      this.showError(error.message);
    }
  }
  
  async updateNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ note: note })
      });
    } catch (error) {
      console.error('Note update error:', error);
    }
  }
  
  updateCartSummary(cart) {
    // Update subtotal
    const subtotalEl = this.container.querySelector('[data-cart-subtotal]');
    if (subtotalEl) {
      subtotalEl.textContent = this.formatMoney(cart.total_price, cart);
    }
    
    // Update total
    const totalEl = this.container.querySelector('[data-cart-total]');
    if (totalEl) {
      totalEl.textContent = this.formatMoney(cart.total_price, cart);
    }
  }
  
  updateItemPrice(itemEl, cart, line) {
    const cartItem = Array.isArray(cart?.items) ? cart.items[line - 1] : null;
    if (!cartItem) return;
    
    const priceEl = itemEl.querySelector('.cart-item__price-final');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(cartItem.final_line_price, cart);
    }
  }
  
  handleEmptyCart() {
    // Reload page to show empty cart state
    window.location.reload();
  }

  reindexCartItems() {
    this.container.querySelectorAll('[data-cart-item]').forEach((item, index) => {
      item.dataset.line = `${index + 1}`;
    });
  }
  
  dispatchCartUpdateEvent(cart) {
    window.CartApi?.setCartCache?.(cart);

    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: cart
    }));
    
    // Update cart count in header badges
    const cartCountBadges = document.querySelectorAll('#custom-header-cart-count, #cart-count, .custom-header__cart-count, .cart-count');
    cartCountBadges.forEach(badge => {
      badge.textContent = cart.item_count;
      if (cart.item_count === 0) {
        badge.classList.add('hidden');
      } else {
        badge.classList.remove('hidden');
      }
    });
    
    // Update cart title if exists
    const cartTitle = document.querySelector('#custom-header-cart-title');
    if (cartTitle) {
      cartTitle.textContent = `Shopping Cart (${cart.item_count})`;
    }
  }
  
  showError(message) {
    CartApi.showToast(message, true);
  }
  
  formatMoney(cents, currencyData) {
    return Money.format(cents, currencyData?.currency);
  }
}

if (!customElements.get('cart-page-section')) {
  class CartPageSection extends HTMLElement {
    connectedCallback() {
      if (!this.manager) {
        this.manager = new CartPageManager(this);
      }
    }
  }
  customElements.define('cart-page-section', CartPageSection);
}

